import React, { useMemo, useState } from 'react';
import { FiSearch, FiEye } from 'react-icons/fi';
import { orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  CsvExportButton,
} from '../../components/UI';
import { useFirestoreCollection, usePagination, useUserDirectory } from '../../hooks';
import styles from './Pets.module.css';

// Schema: pets.{ ownerId, name, species, breed, age, gender, image, photoURL?, birthDate?, ... }

const speciesOptions = [
  { value: 'all', label: 'All' },
  { value: 'dog', label: 'Dogs' },
  { value: 'cat', label: 'Cats' },
  { value: 'bird', label: 'Birds' },
  { value: 'rabbit', label: 'Rabbits' },
  { value: 'other', label: 'Other' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

// Verified 2026-08-21: pets documents carry `avatar` (the profile photo the
// owner picked) and `image`; the other names are legacy fallbacks.
const getPhoto = (pet) =>
  pet.avatar || pet.image || pet.photoURL || pet.imageUrl || pet.photo || null;

const Pets = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const constraints = useMemo(() => [orderBy('createdAt', 'desc'), limit(500)], []);
  const { data: pets, loading, error } = useFirestoreCollection('pets', constraints);
  const directory = useUserDirectory();

  const filtered = useMemo(() => {
    let list = pets;
    if (speciesFilter !== 'all') {
      list = list.filter(
        (p) => (p.species || 'other').toLowerCase() === speciesFilter
      );
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.breed || '').toLowerCase().includes(q) ||
          (p.microchipId || '').toLowerCase().includes(q) ||
          // Resolved owner name / handle / email, not just the uid.
          directory.searchBlobOf(p.ownerId).includes(q) ||
          (p.id || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [pets, speciesFilter, searchQuery, directory]);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(filtered, 25);

  const csvColumns = [
    { key: 'id', label: 'Pet ID' },
    { key: 'name', label: 'Name' },
    { key: 'species', label: 'Species' },
    { key: 'breed', label: 'Breed' },
    { key: 'age', label: 'Age' },
    { key: 'gender', label: 'Gender' },
    { key: 'ownerName', label: 'Owner', accessor: (p) => directory.nameOf(p.ownerId) },
    { key: 'ownerEmail', label: 'Owner Email', accessor: (p) => directory.emailOf(p.ownerId) },
    { key: 'ownerId', label: 'Owner UID' },
    { key: 'microchipId', label: 'Microchip' },
    { key: 'availableForMate', label: 'Available for mate', accessor: (p) => (p.availableForMate ? 'yes' : 'no') },
    { key: 'createdAt', label: 'Created', accessor: (p) => formatDate(p.createdAt) },
  ];

  const columns = [
    {
      key: 'pet',
      title: 'Pet',
      render: (_, row) => (
        <div className={styles.petCell}>
          <div className={styles.avatar}>
            {getPhoto(row) ? <img src={getPhoto(row)} alt="" /> : <span>🐾</span>}
          </div>
          <div>
            <div className={styles.name}>{row.name || '(unnamed)'}</div>
            <div className={styles.meta}>
              {[row.species, row.breed].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'age',
      title: 'Age',
      render: (v) => v || '-',
    },
    {
      key: 'gender',
      title: 'Gender',
      render: (v) => (v ? <Badge variant="secondary">{v}</Badge> : '-'),
    },
    {
      key: 'ownerId',
      title: 'Owner',
      render: (v) => (
        <div>
          <div className={styles.name}>{directory.nameOf(v) || '-'}</div>
          <div className={styles.meta}>{directory.emailOf(v) || directory.handleOf(v) || v || ''}</div>
        </div>
      ),
    },
    { key: 'createdAt', title: 'Added', render: (v) => formatDate(v) },
    {
      key: 'actions',
      title: '',
      width: '60px',
      render: (_, row) => (
        <Button
          variant="ghost"
          size="sm"
          icon={<FiEye />}
          onClick={() => {
            setSelected(row);
            setShowDetail(true);
          }}
        />
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Pets</h1>
          {!loading && <Badge variant="primary">{pets.length} loaded</Badge>}
        </div>
        <p className={styles.subtitle}>
          Browse all pets registered in Dierend
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {error && (
        <Card>
          <div style={{ color: '#f87171' }}>Error loading pets: {error.message}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Search by name, breed, owner UID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={FiSearch}
            />
          </div>
          <div className={styles.filters}>
            {speciesOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${speciesFilter === option.value ? styles.active : ''}`}
                onClick={() => setSpeciesFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CsvExportButton rows={filtered} columns={csvColumns} filename={`dierend-pets-${speciesFilter}`} />
        </div>

        <Table columns={columns} data={pageData} loading={loading} emptyMessage="No pets found" />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        title={selected?.name || 'Pet'}
        size="lg"
      >
        {selected && (
          <div className={styles.detail}>
            {getPhoto(selected) && (
              <div className={styles.cover}>
                <img src={getPhoto(selected)} alt="" />
              </div>
            )}
            <div className={styles.grid}>
              <div className={styles.item}>
                <span className={styles.label}>Name</span>
                <span className={styles.value}>{selected.name || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Species</span>
                <span className={styles.value}>{selected.species || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Breed</span>
                <span className={styles.value}>{selected.breed || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Age</span>
                <span className={styles.value}>{selected.age || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Gender</span>
                <span className={styles.value}>{selected.gender || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Owner</span>
                <span className={styles.value}>{directory.nameOf(selected.ownerId) || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Owner email</span>
                <span className={styles.value}>{directory.emailOf(selected.ownerId) || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Owner UID</span>
                <span className={styles.value}>{selected.ownerId || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Microchip</span>
                <span className={styles.value}>{selected.microchipId || '-'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Pet ID</span>
                <span className={styles.value}>{selected.id}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Created</span>
                <span className={styles.value}>{formatDate(selected.createdAt)}</span>
              </div>
            </div>
            {selected.bio && (
              <div className={styles.section}>
                <h4>Bio</h4>
                <p>{selected.bio}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Pets;
